import { useMemo, useReducer, useRef } from 'react';

type SetterFunction<U> = (value: U) => void;

type ObjectSetters<U> = null extends U
  ? U extends Array<unknown>
    ? Record<string, never>
    : {
        [K in keyof U]: Setters<U[K]> | undefined;
      }
  : U extends Array<unknown>
  ? Record<string, never>
  : {
      [K in keyof U]: Setters<U[K]>;
    };
interface ListSetters<U extends Array<unknown>> {
  list: Setters<U[0]>[];
}
interface Setter<U> {
  set: SetterFunction<U>;
}

export type Setters<U> = Setter<U> &
  ObjectSetters<U> &
  (U extends Array<unknown> ? ListSetters<U> : Record<string, never>);

type Primitive = string | number | boolean | Date;

type Validator<U, T> = (data: U, allValues: T) => string | undefined;

type ObjectValidator<U, T> = {
  [K in keyof U]?: Validators<U[K], T>;
};

export type Validators<U, T> = U extends Array<infer W>
  ? Validators<W, T>
  : U extends Primitive
  ? Validator<U, T>
  : ObjectValidator<U, T>;

type ErrorState = string | undefined;

type ListErrors<U extends Array<unknown>> = Errors<U[0]>[];

type ObjectErrors<U> = {
  [K in keyof U]: Errors<U[K]>;
};

export type Errors<U> = U extends Array<unknown>
  ? ListErrors<U>
  : U extends Primitive
  ? ErrorState
  : ObjectErrors<U>;

/**
 * Recursively traverse fields to build an object with identical shape.
 * All nodes have been replaced with a setter object that contains a
 * function `set` that can be used to updated said node.
 */
const buildSetters = <U,>(
  aggregatedKey: string,
  fields: U,
  baseSetter: <T>(m: { aggregatedKey: string; value: T }) => void
): Setters<U> => {
  if (
    typeof fields !== 'object' ||
    fields === null ||
    fields instanceof Date ||
    fields === undefined
  ) {
    return {
      set: (value: U) => baseSetter<U>({ aggregatedKey, value }),
    } as Setters<U>;
  }
  if (!Array.isArray(fields)) {
    const fieldsObject = fields as unknown as Record<string, U[keyof U]>;
    const nested = Object.keys(fieldsObject).reduce((prev, curr) => {
      return {
        ...prev,
        [curr]: buildSetters<U[keyof U]>(
          aggregatedKey + '.' + curr,
          fieldsObject[curr],
          baseSetter
        ),
      };
    }, {} as ObjectSetters<U>);

    return {
      ...nested,
      set: (value: U) => baseSetter<U>({ aggregatedKey, value }),
    } as Setters<U>;
  } else {
    const fieldsList: Array<unknown> = fields;
    const list = fieldsList.map((f, idx) =>
      buildSetters(aggregatedKey + '.' + idx, f, baseSetter)
    );
    return {
      set: (value: U) => baseSetter<U>({ aggregatedKey, value }),
      list,
    } as unknown as Setters<U>;
  }
};

/**
 * Recursively traverse fields to build an object with identical shape.
 * All leaves have been replaced with an ErrorState that contains the
 * result of applying the respective validator on the value.
 */
const buildErrors = <U, T>(
  aggregatedKey: string,
  fields: U,
  validators: Validators<U, T>,
  changelog: string[],
  allFields: T,
  hasDependentValidators = false
): { errors: Errors<U>; hasErrors: boolean } => {
  if (
    typeof fields !== 'object' ||
    fields === null ||
    fields instanceof Date ||
    fields === undefined
  ) {
    // Always validate the fields whether there is changelog or not.
    // If there is no changelog, we don't want to return any errors for the fields
    // But we still want to return hasErrors value.
    if (validators && typeof validators === 'function') {
      const error = validators(fields, allFields);

      if (
        changelog.length > 0 &&
        (changelog.includes(aggregatedKey) || hasDependentValidators)
      ) {
        return {
          errors: error,
          hasErrors: !!error,
        };
      } else {
        return {
          errors: undefined as Errors<U>,
          hasErrors: !!error,
        };
      }
    }
    return {
      errors: undefined as Errors<U>,
      hasErrors: false,
    };
  }

  if (!Array.isArray(fields)) {
    const fieldsObject = fields as unknown as Record<string, U[keyof U]>;
    const validatorsObject = validators as
      | Record<string, Validators<U[keyof U], T>>
      | undefined;

    let aggregatedHasError = false;
    const nested = Object.keys(fieldsObject).reduce((prev, curr) => {
      const { errors, hasErrors } = validatorsObject
        ? buildErrors(
            aggregatedKey + '.' + curr,
            fieldsObject[curr],
            validatorsObject[curr],
            changelog,
            allFields,
            hasDependentValidators
          )
        : { errors: undefined as Errors<U>, hasErrors: false };

      aggregatedHasError = aggregatedHasError || hasErrors;
      return {
        ...prev,
        [curr]: errors,
      };
    }, {});
    return {
      errors: nested as Errors<U>,
      hasErrors: aggregatedHasError,
    };
  } else {
    const fieldsList: Array<typeof fields> = fields;
    const arrayValidationFunction = validators;
    let aggregatedHasError = false;
    const nested = validators
      ? fieldsList.map((f, idx) => {
          const { errors, hasErrors } = buildErrors(
            aggregatedKey + '.' + idx,
            f,
            arrayValidationFunction,
            changelog,
            allFields,
            hasDependentValidators
          );
          aggregatedHasError = aggregatedHasError || hasErrors;
          return errors;
        })
      : undefined;
    return {
      errors: nested as Errors<U>,
      hasErrors: aggregatedHasError,
    };
  }
};

/**
 * A hook for storing and validating data, for instance that of a form.
 *
 * If something is undefined, it needs to have a default value in structureAndDefaultValues
 * (the default value can be undefined, but needs to be set).
 * @param structureAndDefaultValues The structure of the data that the hook keeps track of.
 * This object is used for building the fields, setters and error objects that are returned.
 * Make sure that all keys are present in the form. I.e. set {someKey: undefined} for values supporting undefined.
 * If two nested object have the same reference they will get the same setter reference.
 * @param validators An object with the same shape as structureAndDefaultValues but
 * with validation functions instead of values.
 * @returns fields: the data to display in your components
 * @returns setters: An object on the same form as fields, but with set functions to update the data
 * @returns errors: An object on the same form as fields, but with the result of the validator on the given field
 * @returns edited: True if any field has been interacted with
 * @returns hasErrors: True if any field has an error according to the validators
 */
export const useForm = <T,>(
  structureAndDefaultValues: T,
  validators: Validators<T, T>,
  options?: {
    hasDependentValidators?: boolean;
  }
) => {
  const changelog = useRef<string[]>([]);

  const [formFields, setFormField] = useReducer<
    (currentFormFields: T, a: { aggregatedKey: string; value: any }) => T
  >((currentFormFields: T, { aggregatedKey, value }) => {
    const path = aggregatedKey.split('.').slice(1);
    if (path.length === 0) {
      return value;
    }

    changelog.current = [...changelog.current, aggregatedKey];

    const newFormFields = Array.isArray(currentFormFields)
      ? [...currentFormFields]
      : { ...currentFormFields };

    let toUpdate: any = newFormFields;

    for (let i = 0; i < path.length - 1; ++i) {
      const currentPath = path[i];
      toUpdate = toUpdate[currentPath];
    }
    const lastKey = path[path.length - 1] as keyof T;
    toUpdate[lastKey] = value;

    return newFormFields;
  }, structureAndDefaultValues);

  const setters = useMemo(() => {
    return buildSetters('', formFields, setFormField);
  }, [formFields, setFormField]);

  const { errors, hasErrors } = useMemo(() => {
    return buildErrors(
      '',
      formFields,
      validators,
      changelog.current,
      formFields,
      options?.hasDependentValidators
    );
  }, [formFields, validators, changelog, options]);

  return {
    fields: formFields,
    setters,
    errors,
    edited: changelog.current.length > 0,
    hasErrors,
  };
};
